<script setup lang="ts">
import GenericError from '~/components/user-interface/generic-error.vue';
import Checkbox from 'primevue/checkbox';
import { ArrowPathIcon } from '@heroicons/vue/24/outline';

const site = useSiteStore();
const { $t, $trackEvent, $dayjs } = useNuxtApp();
const { data, verifyOtp, requestOtp, getMobileNumberStatuses } =
  useMobileNumberHandler();

const isNumberToVerifyOTPExpired = computed(() =>
  $dayjs().isAfter(
    $dayjs(data.numberToVerify?.expiryDateTime)
      .subtract(1, 'day')
      .add(3, 'minutes'),
  ),
);

const { expiryInterval, isDone, reset } = useCountDown({
  expiryDateTime: isNumberToVerifyOTPExpired.value
    ? $dayjs().add(3, 'minutes').format()
    : $dayjs(data.numberToVerify?.expiryDateTime)
        .subtract(1, 'day')
        .add(3, 'minutes')
        .format(),
  minutes: 3,
  daysBack: 1,
});

const token = ref<string>(null);
const isVerified = ref<boolean>(false);
const checked = ref<boolean>(true);
const resendLoading = ref<boolean>(false);

const declineHandler = () => {
  data.numberToVerify = null;
  data.newNumber = '';
  site.deactivateModal();
};

const confirmHandler = async () => {
  if (!!token) {
    const response = await verifyOtp(
      !!data.newNumber ? data.newNumber : data.numberToVerify.mobileNumber,
      token.value,
      false,
    );
    if (response.isSuccessful) {
      $trackEvent('Account', 'MY_ACCOUNT_ADD_SECONDARY_NUMBER');
      isVerified.value = true;
      site.deactivateModal();
    } else {
      data.errorCode = String(response?.error?.displayCode ?? 'general');
      data.errorRef = String(response?.error?.code ?? '');
    }
  }
};

const resendHandler = async () => {
  if (resendLoading.value) return;
  resendLoading.value = true;
  const resendResponse = await requestOtp();
  if (resendResponse.isSuccessful) reset($dayjs().add(3, 'minutes').format());
  resendLoading.value = false;
};

onMounted(() => {
  getMobileNumberStatuses();
  if (isNumberToVerifyOTPExpired.value) requestOtp();
});

onBeforeUnmount(() => {
  data.errorCode = '';
  data.errorRef = '';
  declineHandler();
});
</script>

<template>
  <div
    class="dark:bg-dark-900 space-y-4 w-full flex flex-col text-base-priority p-4"
    @keyup.enter="confirmHandler"
  >
    <LazyUserInterfaceGenericLoader v-if="data.isLoading" :container="true" />
    <span>{{ $t('verify-otp-headline') }}</span>
    <DesignSystemFormOtp @valid="token = $event" @invalid="token = null" />

    <div class="flex gap-1">
      <span :class="{ 'text-red-500': isDone }">
        {{ !isDone ? $t('code-will-expire') : $t('your-code-has-expired') }}
      </span>
      <span v-if="!isDone" class="text-primary-blue-100">
        {{ expiryInterval }}
      </span>
    </div>

    <div class="flex items-center gap-2 pointer-events-none">
      <Checkbox
        v-model="checked"
        input-id="newNumber"
        name="newNumber"
        binary
        :pt="{
          box: {
            class:
              'w-6 border rounded-md dark:border-dark-600 border-light-600 flex items-center justify-center pointer-events-none bg-primary-blue-gradient border-none',
          },
        }"
        :value="true"
      />
      <label for="newNumber">{{ $t('notify-new-secondary') }}</label>
    </div>
    <UserInterfaceModalActionBadge
      bg="dark:bg-dark-700 bg-light-100"
      :left-label="isDone ? 'no-reset-code' : 'resend-code-not-available'"
      :action-text="isDone ? 'try-again' : ''"
      :icon-name="isDone ? ArrowPathIcon : null"
      :disabled="!isDone || resendLoading"
      @action="resendHandler"
    />
    <GenericError
      v-if="data.errorCode"
      state="danger"
      class="max-w-72 mx-auto mb-2"
      :reference-code="data.errorRef"
    >
      {{ $t(`update-details-${data.errorCode}`) }}
    </GenericError>
  </div>

  <div class="flex gap-4 p-4 bg-base w-full">
    <DesignSystemButton
      type="secondary-alternative"
      class="w-full justify-center"
      @click="declineHandler"
    >
      {{ $t('back') }}
    </DesignSystemButton>

    <DesignSystemButton
      type="primary"
      class="w-full justify-center"
      :disabled="!token && !isDone"
      @click="confirmHandler"
    >
      {{ $t('confirm') }}
    </DesignSystemButton>
  </div>
</template>
